/**
 * Contracts for the Portal text CMS (module "cms", owner: ui-shell). Docs: docs/specs/portal-text-cms.md.
 * Staff edit the wording of catalog strings (customer, partner and admin portal, EN/AR) without a deploy.
 *
 * Roles: staff_admin and staff_content edit and publish; ops, support and read-only staff view.
 * The schemas here only describe the wire shape. The rules that decide whether a text may be saved (the key exists
 * in the shipped catalogs, is not protected, plain text only, the same {placeholders} as the shipped string) are
 * enforced by the API with the helpers of @qm/i18n (text-overrides.ts), and mirrored client-side for fast feedback.
 */
import { z } from 'zod';
import { CMS_LOCALES, CmsLocaleSchema, type CmsLocale } from './cms';

export { CMS_LOCALES, CmsLocaleSchema, type CmsLocale };

/** Longest override text. Mirrors MAX_OVERRIDE_LENGTH in @qm/i18n (the API test asserts they match). */
export const UI_TEXT_MAX_LENGTH = 2000;

/** Which part of the product a catalog string belongs to, by catalog origin. */
export const UI_TEXT_SURFACES = ['customer', 'partner', 'admin', 'shared'] as const;
export const UiTextSurfaceSchema = z.enum(UI_TEXT_SURFACES);
export type UiTextSurface = z.infer<typeof UiTextSurfaceSchema>;

export const UiTextKeySchema = z.string().min(3).max(200).regex(/^[A-Za-z0-9][A-Za-z0-9_.-]*$/);

export const UI_TEXT_STATUSES = ['draft', 'published'] as const;
export const UiTextStatusSchema = z.enum(UI_TEXT_STATUSES);
export type UiTextStatus = z.infer<typeof UiTextStatusSchema>;

/** Why a save or publish was refused (error details.reason). */
export const UI_TEXT_REASONS = [
  'unknown_key', 'protected_key', 'empty', 'too_long', 'markup', 'control_chars', 'bidi_controls', 'placeholder_mismatch',
  'stale_version', 'no_override', 'not_published', 'unknown_version',
] as const;
export type UiTextReason = (typeof UI_TEXT_REASONS)[number];

/** GET /v1/cms/admin/ui-text?surface=&q=&locale=&status=&limit=&offset= */
export const UiTextListQuerySchema = z.object({
  surface: UiTextSurfaceSchema.optional(),
  q: z.string().trim().max(200).optional(),
  /** with `status`, only keys that have an override in this locale with that status */
  locale: CmsLocaleSchema.optional(),
  /** draft / published = has such an override; none = still shipped text only */
  status: z.enum(['draft', 'published', 'none']).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
}).strict();
export type UiTextListQuery = z.infer<typeof UiTextListQuerySchema>;

/** PUT /v1/cms/admin/ui-text/:key/:locale: save a draft. baseVersion = the version the editor started from (0 = no override yet). */
export const UiTextSaveSchema = z.object({
  value: z.string().max(10_000),
  baseVersion: z.number().int().min(0),
  note: z.string().trim().max(300).optional(),
}).strict();
export type UiTextSave = z.infer<typeof UiTextSaveSchema>;

/** POST .../publish: publish this version (default: the latest draft). */
export const UiTextPublishSchema = z.object({ version: z.number().int().min(1).optional() }).strict();
/** POST .../rollback: republish an older version. */
export const UiTextRollbackSchema = z.object({ version: z.number().int().min(1) }).strict();

/** One override (one key, one locale). `status` is published only when the latest version is the live one. */
export interface UiTextOverrideView {
  value: string;
  status: UiTextStatus;
  /** latest saved version (draft or published) */
  version: number;
  /** the version that is live, or null when the shipped text is shown */
  publishedVersion: number | null;
  /** the live text (what visitors see), or null when none is published */
  publishedValue: string | null;
  updatedBy: string;
  updatedAt: string;
  publishedAt: string | null;
  publishedBy: string | null;
}

export interface UiTextItemView {
  key: string;
  surfaces: UiTextSurface[];
  /** catalog modules / surface catalogs that ship this key */
  origins: string[];
  shipped: { en: string; ar: string };
  placeholders: { en: string[]; ar: string[] };
  override: { en: UiTextOverrideView | null; ar: UiTextOverrideView | null };
}

export interface UiTextListMeta { total: number; limit: number; offset: number; drafts: number; published: number }

export interface UiTextVersionView { version: number; value: string; createdBy: string; createdAt: string; note: string | null; live: boolean }

export interface UiTextPublishAllResult { published: number; skipped: { key: string; locale: CmsLocale; reason: string }[] }

/** GET /v1/cms/public/ui-text?locale=en|ar. Only published overrides; drafts never leave the admin host. */
export interface UiTextPublic { locale: CmsLocale; version: string; items: Record<string, string> }
export const UI_TEXT_PUBLIC_PATH = '/v1/cms/public/ui-text';
